'use client';

import { useState, FormEvent } from 'react';
import type { Order } from '@/lib/shopify/types';
import OrderList from './order-list';

export default function GuestTrackForm() {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const res = await fetch('/api/track-order/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderNumber, email }),
    });
    const data = await res.json();
    setSubmitting(false);

    if (!res.ok || !data.order) {
      setError(data.error || "We couldn't find that order.");
      setOrder(null);
      return;
    }
    setOrder(data.order);
  }

  return (
    <div className="guest-track">
      {order ? (
        <div className="guest-track-result">
          <div className="guest-track-result-head">
            <span>Order found</span>
            <button
              type="button"
              className="guest-track-reset"
              onClick={() => {
                setOrder(null);
                setOrderNumber('');
                setEmail('');
              }}
            >
              Track another order
            </button>
          </div>
          <OrderList orders={[order]} />
        </div>
      ) : (
        <form onSubmit={onSubmit} className="guest-track-form" noValidate>
          {error && <div className="auth-error">{error}</div>}
          <div className="auth-field">
            <label className="auth-label" htmlFor="track-order-number">Order number</label>
            <input
              id="track-order-number"
              className="auth-input"
              type="text"
              placeholder="#1001"
              required
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
            />
          </div>
          <div className="auth-field">
            <label className="auth-label" htmlFor="track-email">Email used at checkout</label>
            <input
              id="track-email"
              className="auth-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <button className="auth-button" type="submit" disabled={submitting}>
            {submitting ? 'Looking up…' : 'Track order'}
          </button>
        </form>
      )}

      <style>{`
        .guest-track-form {
          max-width: 420px;
          margin: 0 auto;
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem;
          padding: 1.75rem;
        }
        .guest-track-result {
          max-width: 720px;
          margin: 0 auto;
        }
        .guest-track-result-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
          font-size: 0.85rem;
          color: var(--color-text-secondary);
        }
        .guest-track-reset {
          background: transparent;
          border: 1px solid var(--color-border);
          padding: 0.4rem 0.85rem;
          border-radius: 0.5rem;
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--color-primary);
          cursor: pointer;
        }
        .guest-track-reset:hover { border-color: var(--color-primary); }
        .auth-error {
          background: #fdecee;
          color: #a1272c;
          border: 1px solid #f5c2c6;
          padding: 0.65rem 0.8rem;
          border-radius: 0.5rem;
          font-size: 0.85rem;
          margin-bottom: 1rem;
        }
        .auth-field {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          margin-bottom: 1rem;
        }
        .auth-label {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--color-text-primary);
        }
        .auth-input {
          width: 100%;
          padding: 0.65rem 0.8rem;
          font-size: 0.9rem;
          border: 1px solid var(--color-border);
          border-radius: 0.5rem;
          background: var(--color-white);
          color: var(--color-text-primary);
        }
        .auth-input:focus {
          outline: none;
          border-color: var(--color-primary);
          box-shadow: 0 0 0 3px rgba(27,63,35,0.12);
        }
        .auth-button {
          width: 100%;
          padding: 0.75rem 1rem;
          font-size: 0.95rem;
          font-weight: 600;
          background: var(--color-primary);
          color: var(--color-white);
          border: none;
          border-radius: 1rem;
          cursor: pointer;
        }
        .auth-button:hover { background: var(--color-primary-dark); }
        .auth-button:disabled { opacity: 0.6; cursor: not-allowed; }
      `}</style>
    </div>
  );
}
